import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MarketplaceLayout } from "../components/MarketplaceLayout";
import { useI18n } from "../contexts/I18nContext";
import api, { fileUrl } from "../lib/api";
import { User, Instagram, Globe, Twitter } from "lucide-react";

const ensureUrl = (s) => {
  if (!s) return null;
  if (s.startsWith("http")) return s;
  return `https://${s.replace(/^@/, "")}`;
};
const igUrl = (s) => s ? `https://instagram.com/${s.replace(/^@/, "")}` : null;
const xUrl = (s) => s ? `https://x.com/${s.replace(/^@/, "")}` : null;
const ttUrl = (s) => s ? `https://tiktok.com/@${s.replace(/^@/, "")}` : null;
const hasStandardFraming = (artwork) => (artwork.standard_framing || []).some((o) => o.enabled);

export default function ArtistPublicPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/artists/${id}`).then((r) => setData(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <MarketplaceLayout>
        <div className="max-w-3xl mx-auto px-6 py-32 text-center overline text-neutral-400">
          {t.artistPublic.notFound}
        </div>
      </MarketplaceLayout>
    );
  }
  if (!data) {
    return (
      <MarketplaceLayout>
        <div className="max-w-3xl mx-auto px-6 py-32 text-center overline text-neutral-400">Loading...</div>
      </MarketplaceLayout>
    );
  }

  const { artist, artworks } = data;

  return (
    <MarketplaceLayout>
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12" data-testid="artist-public">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <div className="aspect-square bg-neutral-100 overflow-hidden">
              {artist.picture ? (
                <img src={fileUrl(artist.picture)} alt={artist.name} className="w-full h-full object-cover" data-testid="artist-picture" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-neutral-400">
                  <User size={64} strokeWidth={1} />
                </div>
              )}
            </div>
          </div>
          <div className="lg:col-span-8">
            <div className="overline text-neutral-500 mb-4">{t.artistPublic.overline}</div>
            <h1 className="font-serif text-5xl lg:text-7xl font-light tracking-tighter leading-[0.95]" data-testid="artist-name">
              {artist.name}
            </h1>
            {artist.bio && (
              <p className="mt-10 text-base lg:text-lg text-neutral-700 leading-relaxed max-w-2xl" data-testid="artist-bio">
                {artist.bio}
              </p>
            )}

            <div className="mt-8 flex flex-wrap gap-4 items-center" data-testid="artist-socials">
              {artist.instagram && (
                <a href={igUrl(artist.instagram)} target="_blank" rel="noreferrer" className="flex items-center gap-2 overline text-neutral-700 hover:text-black" data-testid="social-instagram">
                  <Instagram size={14} strokeWidth={1.5} /> Instagram
                </a>
              )}
              {artist.website && (
                <a href={ensureUrl(artist.website)} target="_blank" rel="noreferrer" className="flex items-center gap-2 overline text-neutral-700 hover:text-black" data-testid="social-website">
                  <Globe size={14} strokeWidth={1.5} /> Web
                </a>
              )}
              {artist.twitter && (
                <a href={xUrl(artist.twitter)} target="_blank" rel="noreferrer" className="flex items-center gap-2 overline text-neutral-700 hover:text-black" data-testid="social-twitter">
                  <Twitter size={14} strokeWidth={1.5} /> X
                </a>
              )}
              {artist.tiktok && (
                <a href={ttUrl(artist.tiktok)} target="_blank" rel="noreferrer" className="flex items-center gap-2 overline text-neutral-700 hover:text-black" data-testid="social-tiktok">
                  <span className="text-xs font-bold">TT</span> TikTok
                </a>
              )}
            </div>

            <div className="mt-6 overline text-neutral-500">
              {artworks.length} {artworks.length === 1 ? t.artistPublic.work : t.artistPublic.works}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-black/5 py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="overline text-neutral-500 mb-8">{t.artistPublic.archive}</div>
          {artworks.length === 0 ? (
            <div className="py-16 text-center overline text-neutral-400">{t.catalog.empty}</div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-16">
              {artworks.map((a) => {
                const minPrice = a.sizes?.length ? Math.min(...a.sizes.map(s => s.price)) : a.price;
                return (
                  <Link key={a.id} to={`/artwork/${a.id}`} className="artwork-card group" data-testid={`artist-artwork-${a.id}`}>
                    <div className="aspect-[4/5] overflow-hidden bg-neutral-100">
                      <img src={fileUrl(a.image_path)} alt={a.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="mt-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="overline text-neutral-400">{a.category}</div>
                        {hasStandardFraming(a) && (
                          <div className="inline-flex items-center border border-neutral-900 bg-neutral-950 text-white overline text-[9px] px-2 py-1" data-testid={`artist-framing-badge-${a.id}`}>
                            Disponible enmarcado
                          </div>
                        )}
                      </div>
                      <h3 className="font-serif text-xl mt-1">{a.title}</h3>
                      <div className="text-sm mt-1.5">{a.sizes?.length > 1 ? "Desde " : ""}€{minPrice.toFixed(2)}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </MarketplaceLayout>
  );
}
